import 'react-select-me/lib/ReactSelectMe.css';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SelectMe from 'react-select-me';

import { formatCurrency } from '../../../../common-utils/formatters';
import type { AsyncStatus } from '../../../@types/AsyncStatus';
import type { ReferentMutation } from '../../../@types/Company';
import { setupStepOrder } from '../../../@types/Company';
import type { CoproAttributed } from '../../../@types/CoproAttributed';
import type { User } from '../../../@types/User';
import { useDvfYears } from '../../../hooks/useDvfYears';
import {
  addSelectedRow, createAttribution, deleteAttribution,
  fetchCopro,
  fetchCoproAttribution,
  removeSelectedRow, setSelectedRow,
} from '../../../store/CoproReducer';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import type { RootState } from '../../../store/store';
import {
  assignBrandingReferent,
  bulkAssignBrandingReferent,
  delAttribution as delAttributionUrl,
  getAgentsCommerciaux,
  getBrandingReferentMappings,
  getSalaries,
  removeBrandingReferent,
  setAttribution as setAttributionUrl,
} from '../../../utils/api';
import { getValueByLabel, isReferentPubliqueRole, REFERENT_PUBLIQUE_VALUE, roles as allRoles } from '../../../utils/attributions';
import classNames from '../../../utils/classNames';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import { FonctionTypeUserEnum } from '../../../utils/userFunctionsMap';
import { extractUniqueUsersFromFonctionResults } from '../../../utils/users';
import Badge from '../../common/Badge';
import { showToast } from '../../common/Notification';
import Popup from '../../common/Popup';
import { SetupPage, updateSetupState } from '../../common/SetupPage';
import Spinner from '../../common/Spinner';
import Table from '../../common/Table';
import AttributionChooserDialog from './AttributionChooserDialog';
import PerformanceAttributionDialog from './PerformanceAttributionDialog';
import SmartAttributionDialog from './SmartAttributionDialog';
import type { SmartAttributionCluster, SmartAttributionSalesEntry } from './types';

type Role = {
  label: string;
  value: string;
};

type FilterState = {
  ventesMin: number | null;
  ventesMax: number | null;
  caMin: number | null;
  caMax: number | null;
};


const DEFAULT_LOOKBACK = 3;
const MAX_INTERVENANTS_PER_COPRO = 5;
const MAX_TRANSACTIONNAIRES_PER_COPRO = 5;
const MAX_GESTIONNAIRES_LOCATIFS_PER_COPRO = 5;

const INTERVENANT_EXTERIEUR_ROLE_VALUE = getValueByLabel('INTERVENANT EXTÉRIEUR') ?? '';
const TRANSACTIONNAIRE_ROLE_VALUE = getValueByLabel('TRANSACTIONNAIRE') ?? '';
const GESTIONNAIRE_LOCATIF_ROLE_VALUE = getValueByLabel('GESTIONNAIRE LOCATIF') ?? '';

const AGENT_COMMERCIAL_SUFFIX = ' — Agent co.';

const isAgentCommercialForCompany = (user: User | null | undefined, companyId: number | null | undefined): boolean => {
  if (!user || companyId == null) {
    return false;
  }
  return (user.fonctions ?? []).some((fonction) =>
    fonction.type === FonctionTypeUserEnum.AGENT_COMMERCIAL && fonction.company?.id === companyId,
  );
};

const buildUserLabel = (user: User, companyId: number | null | undefined): string => {
  const base = `${user.firstname} ${user.lastname ? user.lastname.toUpperCase() : ''}`.trim();
  return isAgentCommercialForCompany(user, companyId) ? `${base}${AGENT_COMMERCIAL_SUFFIX}` : base;
};

const ActivitySetupStepCollaborator: React.FC = () => {
  const navigate = useNavigate();
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);

  const dispatch = useAppDispatch();

  const copros = useAppSelector((state) => state.copro.copros);
  const selectedRows = useAppSelector((state) => state.copro.selectedRows[setupStepOrder.indexOf('collaboratorAttribution')]);

  const [coproStats, setCoproStats] = useState<Map<number, { nbSales: number; salesAmount: number }>>(new Map());
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [filters, setFilters] = useState<FilterState>({
    ventesMin: null,
    ventesMax: null,
    caMin: null,
    caMax: null,
  });
  const [missingRoleFilters, setMissingRoleFilters] = useState<Set<string>>(new Set());

  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<string | null>(allRoles[0]?.value ?? null);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [selectedBatchBrandingReferent, setSelectedBatchBrandingReferent] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isBatchLoading, setIsBatchLoading] = useState<boolean>(false);
  const [batchErrors, setBatchErrors] = useState<string[]>([]);
  const [companyUsers, setCompanyUsers] = useState<User[]>([]);
  const [companySubcontractorsUsers, setCompanySubcontractorsUsers] = useState<User[]>([]);
  const [isCoproDataLoaded, setIsCoproDataLoaded] = useState(false);
  const [coproBrandingMap, setCoproBrandingMap] = useState<Map<number, string>>(new Map());
  const [isChooserOpen, setIsChooserOpen] = useState(false);
  const [isGeoDialogOpen, setIsGeoDialogOpen] = useState(false);
  const [isPerformanceDialogOpen, setIsPerformanceDialogOpen] = useState(false);
  const [isSmartAttributionPending, setIsSmartAttributionPending] = useState(false);
  const [tableSearchRows, setTableSearchRows] = useState<Array<Record<string, unknown> & { __rowIndex?: number; id?: number }>>([]);

  const [isTop, setIsTop] = useState(true);
  const [isBottom, setIsBottom] = useState(false);
  const topRef = useRef(null);
  const bottomRef = useRef(null);

  const { yearsOptions, yearsAsNumbers, lastYear, status: yearsStatus } = useDvfYears();

  const roles = useMemo(() => allRoles, []);

  const [referentQualite, setReferentQualite] = useState<number>(null);
  const [assistantReferentQualite, setAssistantReferentQualite] = useState<number>(null);
  const [isUpdatingReferents, setIsUpdatingReferents] = useState(false);

  useEffect(() => {
    const topObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsTop(true);
        } else if (!entry.isIntersecting) {
          setIsTop(false);
        }
      },
      { threshold: 1 },
    );

    const bottomObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsBottom(true);
        } else if (!entry.isIntersecting) {
          setIsBottom(false);
        }
      },
      { threshold: 0 },
    );

    if (topRef.current) {
      topObserver.observe(topRef.current);
    }

    if (bottomRef.current) {
      bottomObserver.observe(bottomRef.current);
    }

    return () => {
      if (topRef.current) {
        topObserver.unobserve(topRef.current);
      }
      if (bottomRef.current) {
        bottomObserver.unobserve(bottomRef.current);
      }
    };
  }, [topRef, bottomRef]);

  const fetchCoproStats = useCallback(async(companyId: number, token: string | null, years: number[]) => {
    setIsStatsLoading(true);
    setStatsError(null);

    const params = new URLSearchParams();
    const targetYears = years.length > 0 ? years : [];
    targetYears.forEach((year) => {
      params.append('years[]', String(year));
    });

    try {
      const response = await fetch(`/api/companies/${companyId}/stats/copros/sales/years?${params.toString()}`, JWTHeader(token));
      if (!response.ok) {
        throw new Error('failed-stats');
      }
      const data = await response.json();
      const results = data.results || data;
      if (!Array.isArray(results)) {
        throw new Error('invalid-payload');
      }

      const nextStats = new Map<number, { nbSales: number; salesAmount: number }>();
      results.forEach((item) => {
        if (item && typeof item.id === 'number') {
          nextStats.set(item.id, {
            nbSales: Number(item.nbSales ?? 0),
            salesAmount: Number(item.salesAmount ?? 0),
          });
        }
      });

      setCoproStats(nextStats);
    } catch (error) {
      console.error('Error fetching copro stats :', error);
      setStatsError('Impossible de charger les statistiques de ventes pour vos copropriétés.');
    } finally {
      setIsStatsLoading(false);
    }
  }, []);

  function getCopros() {
    setStatus('PENDING');
    try {
      dispatch(fetchCopro()).unwrap()
        .then(
          () => dispatch(fetchCoproAttribution()).unwrap()
            .then(
              () => {
                setStatus('SUCCESS');
                setIsCoproDataLoaded(true);
              },
            ).catch(() => {
              setStatus('ERROR');
            }),
        ).catch(() => {
          setStatus('ERROR');
        });
    } catch (error) {
      console.error('Error fetching copros :', error);
      setStatus('ERROR');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
    }
  }

  useEffect(() => {
    if (!company || !company.id || !jwt) {
      return;
    }

      getCopros();

      // Fetch branding referent mappings for the company
      fetch(getBrandingReferentMappings(company.id), JWTHeader(jwt))
        .then((response) => response.json())
        .then((data) => {
          const mappings: { coproId: number; brandingReferentId: string }[] = Array.isArray(data) ? data : (data?.data ?? []);
          const map = new Map<number, string>();
          mappings.forEach((m) => {
            map.set(m.coproId, m.brandingReferentId);
          });
          setCoproBrandingMap(map);
        })
        .catch((error) => {
          console.error('Error fetching branding referent mappings:', error);
        });

      // Fetch salariés (excluding agents commerciaux) and agents commerciaux in parallel,
      // then merge them so agents commerciaux are selectable for any role at step 5
      // while still being clearly identifiable in the dropdowns.
      Promise.all([
        fetch(getSalaries(company.id, true), JWTHeader(jwt)).then((response) => response.json()),
        fetch(getAgentsCommerciaux(company.id), JWTHeader(jwt)).then((response) => response.json()),
      ])
        .then(([salariesData, agentsData]) => {
          if (salariesData && salariesData.results) {
            const salariesForCompany = extractUniqueUsersFromFonctionResults(
              salariesData.results,
              (fonctionResult) => fonctionResult.company?.id === company.id,
            );
            const agentsForCompany = (agentsData && agentsData.results)
              ? extractUniqueUsersFromFonctionResults(
                agentsData.results,
                (fonctionResult) => fonctionResult.company?.id === company.id,
              )
              : [];

            const mergedById = new Map<number, User>();
            salariesForCompany.forEach((user) => mergedById.set(user.id, user));
            agentsForCompany.forEach((agent) => {
              const existing = mergedById.get(agent.id);
              if (!existing) {
                mergedById.set(agent.id, agent);
                return;
              }
              const fonctions = [...(existing.fonctions ?? [])];
              (agent.fonctions ?? []).forEach((fonction) => {
                if (!fonctions.some((candidate) => candidate.id === fonction.id)) {
                  fonctions.push(fonction);
                }
              });
              mergedById.set(agent.id, { ...existing, fonctions });
            });

            setCompanyUsers([...mergedById.values()]);
            setCompanySubcontractorsUsers(
              extractUniqueUsersFromFonctionResults(
                salariesData.results,
                (fonctionResult) => fonctionResult.company?.id !== company.id,
              ),
            );

            if (company.referentQualiteUser) {
              setReferentQualite(company.referentQualiteUser.id);
            }
            if (company.assistantReferentQualiteUser) {
              setAssistantReferentQualite(company.assistantReferentQualiteUser.id);
            }
          }
        }).catch((error) => {
          console.error('Error fetching subcontractor users :', error);
          setStatus('ERROR');
          showToast('error', 'Une erreur est survenue lors du chargement des données.');
        });
  }, [company, jwt]);

  useEffect(() => {
    if (!company || !company.id || !jwt) {
      return;
    }

    if (selectedYears.length === 0 && yearsAsNumbers.length > 0) {
      setSelectedYears(yearsAsNumbers.slice(0, DEFAULT_LOOKBACK));
      return;
    }

    if (selectedYears.length === 0) {
      return;
    }

    fetchCoproStats(company.id, jwt, selectedYears);
  }, [company, jwt, fetchCoproStats, selectedYears, yearsAsNumbers]);

  function updateAttribution(attributionId: number, coproId: number, role: string, userId: number | null) {
    try {
      return dispatch(deleteAttribution(attributionId)).unwrap()
        .then(() => {
          if (userId !== null && userId !== undefined) {
            return dispatch(createAttribution({ coproId, role, userId }));
          }
        });
    } catch (error) {
      console.error('Error updating attribution :', error);
      showToast('error', 'Une erreur est survenue lors de l\'enregistrement des données.');
      throw error;
    }
  }

  function handleRoleChange(copro: CoproAttributed, role: Role, selectedUserToChange: User | null) {
    const attribution = copro.attributions.find((attr) => attr.fonctionType === role.value);
    if (attribution) {
      return updateAttribution(attribution.id, copro.id, role.value, selectedUserToChange ? selectedUserToChange.id : null);
    } else if (selectedUserToChange) {
      return dispatch(createAttribution({ coproId: copro.id, role: role.value, userId: selectedUserToChange.id }));
    }
  }

  const isCompanyMultiRole = (roleValue: string) => roleValue === TRANSACTIONNAIRE_ROLE_VALUE
    || roleValue === GESTIONNAIRE_LOCATIF_ROLE_VALUE;
  const isMultiRole = (roleValue: string) => roleValue === INTERVENANT_EXTERIEUR_ROLE_VALUE || isCompanyMultiRole(roleValue);

  const getMultiRoleConfig = (roleValue: string): { maxSelection: number | null; maxSelectionError: string } | null => {
    if (roleValue === INTERVENANT_EXTERIEUR_ROLE_VALUE) {
      return {
        maxSelection: MAX_INTERVENANTS_PER_COPRO,
        maxSelectionError: MAX_INTERVENANTS_PER_COPRO != null
          ? `Vous pouvez sélectionner jusqu'à ${MAX_INTERVENANTS_PER_COPRO} intervenants extérieurs.`
          : '',
      };
    }
    if (roleValue === TRANSACTIONNAIRE_ROLE_VALUE) {
      return {
        maxSelection: MAX_TRANSACTIONNAIRES_PER_COPRO,
        maxSelectionError: MAX_TRANSACTIONNAIRES_PER_COPRO != null
          ? `Vous pouvez sélectionner jusqu'à ${MAX_TRANSACTIONNAIRES_PER_COPRO} transactionnaires.`
          : '',
      };
    }
    if (roleValue === GESTIONNAIRE_LOCATIF_ROLE_VALUE) {
      return {
        maxSelection: MAX_GESTIONNAIRES_LOCATIFS_PER_COPRO,
        maxSelectionError: MAX_GESTIONNAIRES_LOCATIFS_PER_COPRO != null
          ? `Vous pouvez sélectionner jusqu'à ${MAX_GESTIONNAIRES_LOCATIFS_PER_COPRO} gestionnaires locatifs.`
          : '',
      };
    }
    return null;
  };

  function handleSingleRoleChange(copro: CoproAttributed, role: Role, selectedUserChanged: { value: number | null, label: string } | null) {
    if (!selectedUserChanged || selectedUserChanged.value === null || selectedUserChanged.value === undefined) {
      handleRoleChange(copro, role, null);
      return;
    }
    const selectedChangedUser = companyUsers.find((user) => user.id === selectedUserChanged.value) 
      || companySubcontractorsUsers.find((user) => user.id === selectedUserChanged.value);
    handleRoleChange(copro, role, selectedChangedUser || null);
  }

  async function handleRoleMultiChange(
    copro: CoproAttributed,
    role: Role,
    selectedOptions: Array<{ value: number | string; label: string }> | null,
  ) {
    const roleConfig = getMultiRoleConfig(role.value);
    if (!roleConfig) {
      return;
    }

    const nextSelected = Array.isArray(selectedOptions) ? selectedOptions : [];
    if (roleConfig.maxSelection != null && nextSelected.length > roleConfig.maxSelection) {
      showToast('error', roleConfig.maxSelectionError);
      return;
    }

    const selectedIds = new Set(
      nextSelected.map((o) => Number(o.value)).filter((v) => !Number.isNaN(v)),
    );

    const existing = (copro.attributions || []).filter((a) => a.fonctionType === role.value);
    const existingIds = new Set(existing.map((a) => a.user?.id).filter((v) => v != null));

    const pendingOperations: Promise<unknown>[] = [];

    // Delete removed attributions
    existing.forEach((a) => {
      const userId = a.user?.id;
      if (userId != null && !selectedIds.has(userId)) {
        pendingOperations.push(dispatch(deleteAttribution(a.id)).unwrap());
      }
    });

    // Create new attributions
    selectedIds.forEach((id) => {
      if (!existingIds.has(id)) {
        pendingOperations.push(dispatch(createAttribution({ coproId: copro.id, role: role.value, userId: id })).unwrap());
      }
    });

    if (pendingOperations.length > 0) {
      await Promise.all(pendingOperations);
    }
  }

  async function handleBrandingReferentChange(coproId: number, brandingReferentId: string | null) {
    if (!company?.id || !jwt) { return; }

    // Skip the round-trip when the value hasn't changed (avoids hammering the API).
    if ((coproBrandingMap.get(coproId) ?? null) === brandingReferentId) { return; }

    try {
      if (brandingReferentId) {
        const requestInit = JWTHeader(jwt);
        requestInit.headers['Content-Type'] = 'application/json';
        requestInit.method = 'PUT';
        requestInit.body = JSON.stringify({ brandingReferentId });
        const response = await fetch(assignBrandingReferent(company.id, coproId), requestInit);
        if (!response.ok) {
          throw new Error('Failed to assign branding referent');
        }
        setCoproBrandingMap((prev) => {
          const next = new Map(prev);
          next.set(coproId, brandingReferentId);
          return next;
        });
      } else {
        const requestInit = JWTHeader(jwt);
        requestInit.method = 'DELETE';
        const response = await fetch(removeBrandingReferent(company.id, coproId), requestInit);
        if (!response.ok) {
          throw new Error('Failed to remove branding referent');
        }
        setCoproBrandingMap((prev) => {
          const next = new Map(prev);
          next.delete(coproId);
          return next;
        });
      }
    } catch (error) {
      console.error('Error updating branding referent:', error);
      showToast('error', 'Une erreur est survenue lors de la mise à jour du référent publique.');
    }
  }

  const getTransactionnaireRole = () => roles.find((role) => role.value === TRANSACTIONNAIRE_ROLE_VALUE);

  async function handleBatchMultiRoleForCopro(copro: CoproAttributed, role: Role, userId: number | null) {
    if (userId == null) {
      const existing = (copro.attributions || []).filter((a) => a.fonctionType === role.value);
      await Promise.all(existing.map((a) => dispatch(deleteAttribution(a.id)).unwrap()));
      return;
    }

    const alreadyAssigned = (copro.attributions || []).some(
      (a) => a.fonctionType === role.value && a.user?.id === userId,
    );
    if (alreadyAssigned) {
      return;
    }

    const roleConfig = getMultiRoleConfig(role.value);
    const currentCount = (copro.attributions || []).filter((a) => a.fonctionType === role.value).length;
    if (roleConfig?.maxSelection != null && currentCount >= roleConfig.maxSelection) {
      showToast('error', roleConfig.maxSelectionError);
      return;
    }

    await dispatch(createAttribution({ coproId: copro.id, role: role.value, userId })).unwrap();
  }

  const handleBatchAssign = async() => {
    const roleToAssign = roles.find((role) => role.value === selectedRole);
    if (!roleToAssign) {
      showToast('error', 'Veuillez sélectionner un rôle avant de continuer.');
      return;
    }

    setIsBatchLoading(true);
    const errorCopros: string[] = [];

    try {
      if (isReferentPubliqueRole(roleToAssign.value)) {
        const brandingId = selectedBatchBrandingReferent || null;
        const targetCopros = selectedRows
          .map((coproIndex) => copros[coproIndex])
          .filter((copro): copro is CoproAttributed => copro != null);
        const coproIds = targetCopros.map((copro) => copro.id);

        if (company?.id && jwt && coproIds.length > 0) {
          // One request for the whole selection instead of one per copro (rate limit).
          const requestInit = JWTHeader(jwt);
          requestInit.headers['Content-Type'] = 'application/json';
          requestInit.method = 'PUT';
          requestInit.body = JSON.stringify({ coproIds, brandingReferentId: brandingId });
          const response = await fetch(bulkAssignBrandingReferent(company.id), requestInit);
          if (response.ok) {
            setCoproBrandingMap((prev) => {
              const next = new Map(prev);
              coproIds.forEach((id) => {
                if (brandingId) { next.set(id, brandingId); } else { next.delete(id); }
              });
              return next;
            });
          } else {
            targetCopros.forEach((copro) => errorCopros.push(copro.name));
          }
        }
      } else {
        const shouldUseMultiSelection = isMultiRole(roleToAssign.value);
        const batchPromises = selectedRows.map(async(coproIndex) => {
          if (copros[coproIndex]) {
            try {
              if (shouldUseMultiSelection) {
                await handleBatchMultiRoleForCopro(copros[coproIndex], roleToAssign, selectedUser);
              } else {
                const mainUser = companyUsers.find((user) => user.id === selectedUser);
                const subcontractorUser = companySubcontractorsUsers.find((user) => user.id === selectedUser);
                const user = mainUser || subcontractorUser;
                await handleRoleChange(copros[coproIndex], roleToAssign, user);
              }
            } catch {
              errorCopros.push(copros[coproIndex].name);
            }
          }
        });
        await Promise.all(batchPromises);
      }

      // Re-sync attributions from the server so the table reflects the batch
      // result without requiring a manual page refresh.
      await dispatch(fetchCoproAttribution()).unwrap();
    } catch (error) {
      console.error('Error during batch attribution:', error);
      showToast('error', 'Une erreur est survenue lors de l\'attribution en lot.');
    } finally {
      // Always tear down the loader/modal, even if an unexpected error occurred,
      // so the spinner popup can never stay stuck open.
      setIsBatchLoading(false);
      setIsPopupOpen(false);
    }

    if (errorCopros.length > 0) {
      setBatchErrors(errorCopros);
    }
  };

  const periodLabel = useMemo(() => {
    if (selectedYears.length === 1) {
      return `${selectedYears[0]}`;
    }
    if (selectedYears.length > 1) {
      const minYear = Math.min(...selectedYears);
      const maxYear = Math.max(...selectedYears);
      return `${minYear}-${maxYear}`;
    }
    return lastYear ? `${lastYear - 2}-${lastYear}` : '3 ans';
  }, [selectedYears, lastYear]);

  const headers = useMemo(() => {
    return ['N° IMMAT', 'NOM DE LA COPROPRIÉTÉ', 'ADRESSE', `NB VENTES (${periodLabel})`, `CA (${periodLabel})`, ...roles.map((role) => role.label), 'MANDANT'];
  }, [periodLabel]);

  const salesCountHeader = headers[3];
  const salesAmountHeader = headers[4];

  const toggleMissingRoleFilter = useCallback((roleValue: string) => {
    setMissingRoleFilters((prev) => {
      const next = new Set(prev);
      if (next.has(roleValue)) {
        next.delete(roleValue);
      } else {
        next.add(roleValue);
      }
      return next;
    });
  }, []);

  const filteredEntries = useMemo(() => copros
    .map((copro, index) => ({ copro, index }))
    .filter(({ copro }) => {
      const stats = coproStats.get(copro.id);
      const nbSales = stats?.nbSales ?? 0;
      const salesAmount = stats?.salesAmount ?? 0;

      if (filters.ventesMin != null && nbSales < filters.ventesMin) {
        return false;
      }
      if (filters.ventesMax != null && nbSales > filters.ventesMax) {
        return false;
      }
      if (filters.caMin != null && salesAmount < filters.caMin) {
        return false;
      }
      if (filters.caMax != null && salesAmount > filters.caMax) {
        return false;
      }

      if (missingRoleFilters.size > 0) {
        for (const roleValue of missingRoleFilters) {
          if (isReferentPubliqueRole(roleValue)) {
            if (coproBrandingMap.has(copro.id)) {
              return false;
            }
          } else {
            const hasRole = (copro.attributions || []).some((attr) => attr.fonctionType === roleValue);
            if (hasRole) {
              return false;
            }
          }
        }
      }

      return true;
    }), [copros, coproStats, filters, missingRoleFilters, coproBrandingMap]);

  const displayedIndexes = useMemo(() => {
    const baseIndexes = filteredEntries.map((entry) => entry.index);
    if (tableSearchRows.length === 0) {
      return baseIndexes;
    }
    const set = new Set<number>();
    tableSearchRows.forEach((row) => {
      if (typeof row.__rowIndex === 'number') {
        set.add(row.__rowIndex);
      } else {
        const match = filteredEntries.find((entry) => entry.copro.id === row.id);
        if (match) {
          set.add(match.index);
        }
      }
    });
    return [...set];
  }, [filteredEntries, tableSearchRows]);

  const allDisplayedSelected = useMemo(() => displayedIndexes.length > 0
    && displayedIndexes.every((index) => selectedRows.includes(index)), [displayedIndexes, selectedRows]);

  const selectAllDisabled = displayedIndexes.length === 0;
  const selectAllLabel = allDisplayedSelected ? 'Désélectionner tout' : 'Sélectionner tout';

  useEffect(() => {
    setTableSearchRows([]);
  }, [filteredEntries]);

  const handleTableFilteredData = useCallback((rows: Array<Record<string, unknown> & { __rowIndex?: number; id?: number }>) => {
    setTableSearchRows(rows ?? []);
  }, []);

  const userOptions = useMemo(() => [...companyUsers]
    .sort((candidateA, candidateB) => candidateA.lastname.localeCompare(candidateB.lastname))
    .map((user) => ({
      value: user.id,
      label: buildUserLabel(user, company?.id),
    })), [companyUsers, company?.id]);

  const options = useMemo(() => [{ value: null, label: 'Désattribuer' }, ...userOptions], [userOptions]);

  const brandingReferents: ReferentMutation[] = useMemo(
    () => (company?.brandingSettings?.referents ?? []).filter((r) => r.id && r.firstname && r.lastname),
    [company?.brandingSettings?.referents],
  );

  const brandingReferentOptions = useMemo(() => [
    { value: null as string | null, label: 'Désattribuer' },
    ...brandingReferents.map((r) => ({
      value: r.id,
      label: `${r.title ?? ''} ${r.firstname} ${r.lastname}`.trim(),
    })),
  ], [brandingReferents]);

  const tableRows = filteredEntries.map(({ copro, index }, position) => {
    const stats = coproStats.get(copro.id);
    const hasStats = stats != null;
    const isBottom8Layers = position >= filteredEntries.length - 8;
    const shouldShowStatsLoadingPlaceholder = isStatsLoading && !hasStats;

    const rawSalesAmount = hasStats ? stats.salesAmount ?? 0 : null;
    const rawSalesCount = hasStats ? stats.nbSales ?? 0 : null;
    const displayedSalesCount = shouldShowStatsLoadingPlaceholder
      ? 'Chargement...'
      : (hasStats
        ? rawSalesCount
        : '—');
    const displayedSalesAmount = shouldShowStatsLoadingPlaceholder
      ? 'Chargement...'
      : (hasStats
        ? formatCurrency(rawSalesAmount ?? 0)
        : '—');

    const row: Record<string, unknown> & { __rowIndex: number; id: number; rawData: CoproAttributed } = {
      'N° IMMAT': copro.remoteId,
      'NOM DE LA COPROPRIÉTÉ': copro.name,
      id: copro.id,
      ADRESSE: copro.addresses.length > 0
        ? `${copro.addresses[0].address} ${copro.addresses[0].postalCode} ${copro.addresses[0].city}`
        : '',
      [salesCountHeader]: hasStats ? rawSalesCount : undefined,
      [salesAmountHeader]: hasStats ? rawSalesAmount : undefined,
      [`${salesCountHeader}__display`]: displayedSalesCount,
      [`${salesAmountHeader}__display`]: displayedSalesAmount,
      MANDANT: '',
      rawData: copro,
      __rowIndex: index,
    };

    const isSubcontracted = copro.company.id !== company.id && copro.company.subcontractors.some((subcontractor) => subcontractor.id === company.id);

    row.MANDANT = isSubcontracted
      ? (
        <div className="text-xs text-black">
          {copro.company.name}
        </div>
      ) : '';

    roles.forEach((role) => {
      if (copro.company.id !== company.id) {
        return;
      }

      const isIntervenant = role.value === INTERVENANT_EXTERIEUR_ROLE_VALUE;
      const isStandardMultiRole = isCompanyMultiRole(role.value);
      const roleAttributionsForCopro = (copro.attributions || [])
        .filter((assignment) => assignment.fonctionType === role.value)
        .sort((a, b) => a.id - b.id);
      const intervenantsForCopro = isIntervenant
        ? roleAttributionsForCopro
        : [];

      if (isIntervenant) {
        let optionsForRole: { value: number | null; label: string }[] = [];
        if (copro.company.subcontractors.some((subcontractor) => subcontractor.id !== company.id)) {
          optionsForRole = companySubcontractorsUsers
            .sort((candidateA, candidateB) => {
              const intervenantFonctionA = candidateA.fonctions.find((candidateFonction) => candidateFonction.company.id !== company.id);
              const intervenantFonctionB = candidateB.fonctions.find((candidateFonction) => candidateFonction.company.id !== company.id);
              const intervenantCompanyA = intervenantFonctionA?.company.name || '';
              const intervenantCompanyB = intervenantFonctionB?.company.name || '';
              const lastnameA = candidateA.lastname || '';
              const lastnameB = candidateB.lastname || '';

              if (intervenantCompanyA.localeCompare(intervenantCompanyB) === 0) {
                return lastnameA.localeCompare(lastnameB);
              }
              return intervenantCompanyA.localeCompare(intervenantCompanyB);
            })
            .map((candidate) => {
              const intervenantFonctionForCandidate = candidate.fonctions.find((candidateFonction) => candidateFonction.company.id !== company.id);
              const intervenantCompanyName = intervenantFonctionForCandidate?.company.name || '';
              return {
                value: candidate.id,
                label: `${intervenantCompanyName} - ${candidate.firstname} ${candidate.lastname ? candidate.lastname.toUpperCase() : ''}`,
              };
            });
        }

        if (companySubcontractorsUsers.length === 0) {
          row[role.label] = 'Aucun sous-traitant';
        } else {
          row[role.label] = (
            <SelectMe
              key={`intervenant-multi-${copro.id}`}
              options={optionsForRole}
              value={intervenantsForCopro.map((assignment) => {
                const userId = assignment.user.id;
                const subcontractor = companySubcontractorsUsers.find((candidate) => candidate.id === userId);
                const intervenantFonction = subcontractor ? subcontractor.fonctions.find((candidateFonction) => candidateFonction.company.id !== company.id) : null;
                const intervenantLabel = subcontractor && intervenantFonction
                  ? `${intervenantFonction.company.name} - ${subcontractor.firstname} ${subcontractor.lastname ? subcontractor.lastname.toUpperCase() : ''}`
                  : `${assignment.user.firstname} ${assignment.user.lastname ? assignment.user.lastname.toUpperCase() : ''}`;
                return { value: userId, label: intervenantLabel };
              })}
              multiple
              closeOnSelect={false}
              onChange={(selected) => {
                const next = Array.isArray(selected) ? selected : [];
                void handleRoleMultiChange(copro, role, next as Array<{ value: number | string; label: string }>);
              }}
              placeholder="Sélectionner"
              listPosition={isBottom8Layers ? 'top' : 'bottom'}
              listMaxHeight={300}
            />
          );
        }
      } else if (isReferentPubliqueRole(role.value)) {
        const mappedBrandingId = coproBrandingMap.get(copro.id) ?? null;
        const matchedReferent = mappedBrandingId
          ? brandingReferentOptions.find((o) => o.value === mappedBrandingId)
          : null;
        const isOrphanedMapping = mappedBrandingId != null && matchedReferent == null;

        if (brandingReferents.length === 0) {
          row[role.label] = <span className="text-xs text-gray-400 italic">Aucun référent de marque</span>;
        } else {
          row[role.label] = (
            <div>
              <SelectMe
                options={brandingReferentOptions}
                value={matchedReferent ?? { value: null, label: isOrphanedMapping ? `<span role="img" aria-label="emoji">⚠</span> Référent supprimé` : 'Désattribuer' }}
                onChange={(selected) => {
                  const option = Array.isArray(selected) ? selected[0] : selected;
                  handleBrandingReferentChange(copro.id, option?.value ?? null);
                }}
                placeholder="Sélectionner"
                isClearable
                listPosition={isBottom8Layers ? 'top' : 'bottom'}
                listMaxHeight={300}
              />
              {isOrphanedMapping && (
                <span className="mt-0.5 block text-[10px] text-amber-600">Référent supprimé de l'image de marque</span>
              )}
            </div>
          );
        }
      } else if (isStandardMultiRole) {
        row[role.label] = (
          <SelectMe
            key={`role-multi-${role.value}-${copro.id}`}
            options={userOptions}
            value={roleAttributionsForCopro.map((assignment) => {
              const userId = assignment.user.id;
              const assignedUser = companyUsers.find((candidate) => candidate.id === userId);
              const assignmentLabel = assignedUser
                ? buildUserLabel(assignedUser, company.id)
                : `${assignment.user.firstname} ${assignment.user.lastname ? assignment.user.lastname.toUpperCase() : ''}`;
              return { value: userId, label: assignmentLabel };
            })}
            multiple
            closeOnSelect={false}
            onChange={(selected) => {
              const next = Array.isArray(selected) ? selected : [];
              void handleRoleMultiChange(copro, role, next as Array<{ value: number | string; label: string }>);
            }}
            placeholder="Sélectionner"
            listPosition={isBottom8Layers ? 'top' : 'bottom'}
            listMaxHeight={300}
          />
        );
      } else {
        const selectedValue = copro.attributions && copro.attributions.find((attribution) => attribution.fonctionType === role.value)?.user;
        const selectedFullUser = selectedValue && selectedValue.id != null
          ? companyUsers.find((candidate) => candidate.id === selectedValue.id)
          : null;
        row[role.label] = (
          <SelectMe
            options={options}
            value={selectedValue && selectedValue.id != null
              ? {
                value: selectedValue.id,
                label: selectedFullUser
                  ? buildUserLabel(selectedFullUser, company.id)
                  : `${selectedValue.firstname} ${selectedValue.lastname ? selectedValue.lastname.toUpperCase() : ''}`,
              }
              : { value: null, label: 'Désattribuer' }}
            onChange={(selectedUserChangedId) => {
              const selected = Array.isArray(selectedUserChangedId) ? selectedUserChangedId[0] : selectedUserChangedId;
              handleSingleRoleChange(copro, role, selected ? { value: typeof selected.value === 'number' ? selected.value : null, label: selected.label } : { value: null, label: '' });
            }}
            placeholder="Sélectionner"
            isClearable
            listPosition={isBottom8Layers ? 'top' : 'bottom'}
            listMaxHeight={300}
          />
        );
      }
    });

    row.VALIDE = selectedRows.includes(index);
    return row;
  });

  const smartAttributionSalesEntries = useMemo<SmartAttributionSalesEntry[]>(() => copros
    .map((copro) => {
      const stats = coproStats.get(copro.id);
      if (!stats) {
        return null;
      }

      const primaryAddress = copro.addresses?.[0];

      return {
        id: copro.id,
        immatriculation: copro.remoteId ?? '',
        name: copro.name ?? '',
        address: primaryAddress?.address ?? '',
        city: primaryAddress?.city ?? '',
        cp: primaryAddress?.postalCode ?? '',
        salesCount: stats.nbSales ?? 0,
        salesAmount: stats.salesAmount ?? 0,
        nbLots: null,
      } as SmartAttributionSalesEntry;
    })
    .filter((entry): entry is SmartAttributionSalesEntry => Boolean(entry)), [copros, coproStats]);

  const isSmartAttributionReady = !isStatsLoading && smartAttributionSalesEntries.length > 0;
  const isSmartAttributionButtonDisabled = isSmartAttributionPending || companyUsers.length === 0 || !isSmartAttributionReady;
  const smartAttributionButtonTitle = (() => {
    if (isSmartAttributionPending) {
      return 'Attribution intelligente en cours…';
    }
    if (companyUsers.length === 0) {
      return 'Aucun collaborateur disponible pour l’attribution.';
    }
    if (isStatsLoading) {
      return 'Les données de ventes sont en cours de chargement.';
    }
    if (smartAttributionSalesEntries.length === 0) {
      return 'Aucune donnée de ventes disponible pour l’attribution intelligente.';
    }
    return undefined;
  })();

  const applySmartAttribution = async(assignments: SmartAttributionCluster[]) => {
    const transactionnaireRole = getTransactionnaireRole();
    if (!transactionnaireRole) {
      showToast('error', 'Le rôle de transactionnaire est introuvable.');
      return;
    }

    setIsSmartAttributionPending(true);

    try {
      const coprosById = new Map<number, CoproAttributed>();
      copros.forEach((copro) => {
        coprosById.set(copro.id, copro);
      });

      const transactions = assignments.flatMap((cluster) => cluster.assignments.map((assignment) => ({
        userId: cluster.userId,
        coproId: assignment.coproId,
      })));

      const applyPromises = transactions.map(async({ userId, coproId }) => {
        const copro = coprosById.get(coproId);
        if (!copro) {
          return;
        }

        const existing = copro.attributions.find((attr) => attr.fonctionType === transactionnaireRole.value);
        if (existing) {
          const deleteInit = JWTHeader(jwt);
          deleteInit.headers['Content-Type'] = 'application/json';
          deleteInit.method = 'DELETE';
          await fetch(delAttributionUrl(existing.id), deleteInit);
        }

        const createInit = JWTHeader(jwt);
        createInit.headers['Content-Type'] = 'application/json';
        createInit.method = 'POST';
        createInit.body = JSON.stringify({ fonctionType: transactionnaireRole.value, user: userId, toNotifyForNewDpe: true });
        await fetch(setAttributionUrl(coproId), createInit);
      });

      await Promise.all(applyPromises);

      await dispatch(fetchCoproAttribution()).unwrap();

      const assignedCoproIds = new Set(transactions.map((assignment) => assignment.coproId));
      const keepSelected = selectedRows.filter((rowIndex) => {
        const copro = copros[rowIndex];
        if (!copro) {
          return false;
        }
        return !assignedCoproIds.has(copro.id);
      });
      dispatch(setSelectedRow({ step: 'collaboratorAttribution', rows: keepSelected }));

      showToast('success', 'Les transactionnaires ont été attribués selon la proposition.');
    } catch (error) {
      console.error('Error applying smart attribution', error);
      showToast('error', 'Une erreur est survenue lors de l’attribution intelligente.');
    } finally {
      setIsSmartAttributionPending(false);
    }
  };

  const handleTableDataChange = useCallback((checked: boolean, id: string) => {
    const numericId = Number(id);
    const entry = filteredEntries.find((e) => e.copro.id === numericId);
    if (!entry) {
      return;
    }

    const originalIndex = entry.index;
    if (checked) {
      dispatch(addSelectedRow({ step: 'collaboratorAttribution', row: originalIndex }));
    } else {
      dispatch(removeSelectedRow({ step: 'collaboratorAttribution', row: originalIndex }));
    }
  }, [dispatch, filteredEntries]);

  const handleSelectAll = () => {
    if (selectAllDisabled) {
      return;
    }
    if (allDisplayedSelected) {
      const remaining = selectedRows.filter((rowIndex) => !displayedIndexes.includes(rowIndex));
      dispatch(setSelectedRow({ step: 'collaboratorAttribution', rows: remaining }));
    } else {
      const union = new Set([...selectedRows, ...displayedIndexes]);
      dispatch(setSelectedRow({ step: 'collaboratorAttribution', rows: [...union] }));
    }
  };

  const handleNextStep = async() => {
    if (status === 'PENDING' || status === 'IDLE') {
      return;
    }
    await updateSetupState(company, jwt, 'collaboratorAttribution', dispatch);
    navigate(ROUTES.SETUP_CONFIGURE_NOTIFICATIONS);
  };

  const renderSaveButton = () => (
    <div
      className={classNames(
        'text-right w-full md:w-auto mt-10',
        !isTop && !isBottom && 'fixed z-[1000] right-0 bottom-0 md:right-5 md:bottom-5 bg-white rounded',
      )}
    >
      <button
        onClick={handleNextStep}
        className={classNames(
          'bg-rgpd-green text-white text-xl px-8 py-4 shadow-md align-center w-full md:w-auto rounded',
          (status === 'PENDING' || status === 'IDLE') && 'opacity-80 cursor-not-allowed',
        )}
      >
        Prochaine étape
      </button>
    </div>
  );

  const isIntervenantRoleSelected = selectedRole === INTERVENANT_EXTERIEUR_ROLE_VALUE;
  const isReferentPubliqueRoleSelected = selectedRole === REFERENT_PUBLIQUE_VALUE;

  const subcontractorOptionsForSelect = useMemo(() => [...companySubcontractorsUsers]
    .sort((a, b) => {
      const subcontractorFonctionA = a.fonctions.find((candidateFonction) => candidateFonction.company.id !== company?.id);
      const subcontractorFonctionB = b.fonctions.find((candidateFonction) => candidateFonction.company.id !== company?.id);
      const companyA = subcontractorFonctionA?.company.name ?? '';
      const companyB = subcontractorFonctionB?.company.name ?? '';
      const lastnameA = a.lastname ?? '';
      const lastnameB = b.lastname ?? '';

      if (companyA.localeCompare(companyB) === 0) {
        return lastnameA.localeCompare(lastnameB);
      }
      return companyA.localeCompare(companyB);
    })
    .map((user) => {
      const intervenantFonction = user.fonctions.find((candidateFonction) => candidateFonction.company.id !== company?.id);
      const intervenantCompanyName = intervenantFonction?.company.name ?? '';
      return {
        value: String(user.id),
        label: `${intervenantCompanyName} - ${user.firstname} ${user.lastname ? user.lastname.toUpperCase() : ''}`,
      };
    }), [companySubcontractorsUsers, company?.id]);

  const batchUserOptions = useMemo(() => {
    if (isReferentPubliqueRoleSelected) {
      return [
        { value: '', label: 'Désattribuer' },
        ...brandingReferents.map((r) => ({
          value: r.id,
          label: `${r.title ?? ''} ${r.firstname} ${r.lastname}`.trim(),
        })),
      ];
    }
    if (isIntervenantRoleSelected) {
      return [{ value: '', label: 'Désattribuer' }, ...subcontractorOptionsForSelect];
    }
    return options.map((option) => ({
      value: option.value == null ? '' : String(option.value),
      label: option.label,
    }));
  }, [isReferentPubliqueRoleSelected, isIntervenantRoleSelected, subcontractorOptionsForSelect, options, brandingReferents]);

  const mergedCellsConfig = (item: { rawData: CoproAttributed }) => {
    const config: { [header: string]: { colSpan: number; content: string | JSX.Element } } = {};

    if (item.rawData.company && item.rawData.company.subcontractors) {
      const shouldMerge = item.rawData.company.subcontractors.some((subcontractor) => subcontractor.id === company.id);

      if (shouldMerge && item && item.rawData && item.rawData.attributions) {
        const coproUsersAttributed = item.rawData.attributions
          .filter((attribution) => attribution.fonctionType === INTERVENANT_EXTERIEUR_ROLE_VALUE)
          .map((attribution) => `${attribution.user.firstname} ${attribution.user.lastname ? attribution.user.lastname.toUpperCase() : ''}`)
          .join(', ');

        config['RÉFÉRENT PUBLIQUE'] = {
          colSpan: 7,
          content: coproUsersAttributed
            ? (
              <div className="text-center">
                Copropriété gérée par
                <b>{coproUsersAttributed}</b>
                .
              </div>
            )
            : <div className="text-center">Cette copropriété n'a pas encore de responsable.</div>,
        };

        config['CHARGÉ QUALIF DPE'] = { colSpan: 6, content: '' };
        config['GESTIONNAIRE DE L\'IMMEUBLE'] = { colSpan: 6, content: '' };
        config.TRANSACTIONNAIRE = { colSpan: 6, content: '' };
        config['GESTIONNAIRE LOCATIF'] = { colSpan: 6, content: '' };
        config['RESPONSABLE DU DOCUMENT D\'AG'] = { colSpan: 6, content: '' };
        config['INTERVENANT EXTÉRIEUR'] = { colSpan: 6, content: '' };
      }
    }

    return config;
  };

  const sortableColumns = useMemo(() => {
    const columns: { [key: string]: string } = {
      'N° IMMAT': 'string',
      'NOM DE LA COPROPRIÉTÉ': 'string',
      NOM: 'string',
      ADRESSE: 'string',
      [salesCountHeader]: 'number',
      [salesAmountHeader]: 'number',
      MANDANT: 'string',
      'RÉFÉRENT PUBLIQUE': 'string',
      'CHARGÉ QUALIF DPE': 'string',
      'GESTIONNAIRE DE L\'IMMEUBLE': 'string',
      'RESPONSABLE DU DOCUMENT D\'AG': 'string',
      'INTERVENANT EXTÉRIEUR': 'string',
      TRANSACTIONNAIRE: 'string',
      'GESTIONNAIRE LOCATIF': 'string',
    };
    return columns;
  }, [salesAmountHeader, salesCountHeader]);

  const tableRef = useRef(null);

  useEffect(() => {
    const handleMenuOpen = (mutationList: MutationRecord[]) => {
      mutationList.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const targetElement = mutation.target as Element;
          const isOpen = targetElement.classList?.contains('dd__opened');
          if (isOpen) {
            document.getElementsByClassName('overflow-x-auto')[0].scrollTo({
              top: document.getElementsByClassName('overflow-x-auto')[0].scrollTop + 1000,
            });
          }
        }
      });
    };

    const observer = new MutationObserver(handleMenuOpen);

    if (tableRef.current) {
      const selectWrappers = tableRef.current.querySelectorAll('.dd__wrapper');
      selectWrappers.forEach((wrapper) => {
        observer.observe(wrapper, {
          attributes: true,
          attributeFilter: ['class'],
        });
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [copros, companyUsers, status]);

  const handleReferentQualiteUpdate = async(newReferentQualite: number | null, newAssistantReferentQualite: number | null) => {
    if (!company || !jwt) {
      return;
    }
    
    setIsUpdatingReferents(true);
    try {
      const requestInit = JWTHeader(jwt);
      requestInit.headers['Content-Type'] = 'application/json';
      requestInit.method = 'PATCH';
      requestInit.body = JSON.stringify({
        referentQualiteUser: newReferentQualite,
        assistantReferentQualiteUser: newAssistantReferentQualite,
      });
      
      const response = await fetch(`/api/companies/${company.id}`, requestInit);
      
      if (response.ok) {
        showToast('success', 'Les référents qualité ont été mis à jour avec succès.');
      } else {
        showToast('error', 'Une erreur est survenue lors de la mise à jour des référents qualité.');
      }
    } catch (error) {
      console.error('Error updating referent qualité:', error);
      showToast('error', 'Une erreur est survenue lors de la mise à jour des référents qualité.');
    } finally {
      setIsUpdatingReferents(false);
    }
  };

  const selectedIndexesSet = useMemo(() => new Set(selectedRows), [selectedRows]);
  const selectedInFilterCount = useMemo(() => displayedIndexes.filter((idx) => selectedIndexesSet.has(idx)).length, [displayedIndexes, selectedIndexesSet]);
  const totalVisible = displayedIndexes.length;
  const totalCopros = copros.length;

  return (
    <SetupPage
      step={'collaboratorAttribution'}
      title="ATTRIBUER DES RÔLES POUR CHAQUE COPROPRIÉTÉ"
      children={(
        <>
          <div ref={topRef} style={{ height: '1px' }} />
          
          <div className="mb-8 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
            <p className="mb-6 text-lg leading-relaxed font-medium text-gray-800">
                Pour une gestion optimale,{' '}
                <span className="font-bold text-rgpd-blue-light">veuillez attribuer les rôles suivants :</span>
            </p>

            <div className="mt-8 border-t border-gray-100 pt-6">
              <h2 className="flex flex-col items-start gap-3 text-xl font-bold text-gray-800 md:flex-row md:items-center">
                <span className="rounded bg-rgpd-blue-light px-3 py-1 text-sm tracking-wide whitespace-nowrap text-white uppercase">ÉTAPE N°5 :</span>
                <span>ATTRIBUER DES RÔLES POUR CHAQUE COPROPRIÉTÉ</span>
              </h2>
            </div>
          </div>

          <div className="my-8 space-y-6">
            <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold text-rgpd-blue-light uppercase">Référents Mutation Location de l'entreprise</h2>
              <ul className="mb-4 list-inside list-disc space-y-2 text-gray-700">
                <li>C'est lui qui sera en charge de qualifier les DPEs et gérer les premiers contacts avec les copropriétaires.</li>
                <li>Assistant référent mutation location : Elle peut effectuer les memes tâches que le référent mutation location.</li>
              </ul>
              <p className="my-4 text-gray-600 italic">
                Les référents mutation location sont attribués au niveau de votre entreprise.<br/>
                Veuillez désigner ci-dessous le référent mutation location principal et son assistant(e) le cas échéant.
              </p>
              
              <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label className="mb-2 block font-semibold text-gray-800">Référent Mutation Location Principal :</label>
                  <SelectMe
                    options={options}
                    value={referentQualite != null
                      ? options.find((option) => option.value === referentQualite)
                      : { value: null, label: 'Sélectionner un utilisateur' }}
                    onChange={(option) => {
                      const selected = Array.isArray(option) ? option[0] : option;
                      setReferentQualite(selected?.value as number);
                      handleReferentQualiteUpdate(selected?.value as number, assistantReferentQualite);
                    }}
                    placeholder="Sélectionner un utilisateur"
                  />
                </div>
                
                <div>
                  <label className="mb-2 block font-semibold text-gray-800">Assistant(e) Référent Mutation Location :</label>
                  <SelectMe
                    options={options}
                    value={assistantReferentQualite != null
                      ? options.find((option) => option.value === assistantReferentQualite)
                      : { value: null, label: 'Sélectionner un utilisateur' }}
                    onChange={(option) => {
                      const selected = Array.isArray(option) ? option[0] : option;
                      setAssistantReferentQualite(selected?.value as number);
                      handleReferentQualiteUpdate(referentQualite, selected?.value as number);
                    }}
                    placeholder="Sélectionner un utilisateur"
                  />
                </div>
              </div>
              
              {isUpdatingReferents && (
                <div className="mt-4 flex justify-center">
                  <Spinner size="sm" />
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="mt-16 mb-6 text-xl font-semibold text-rgpd-blue-light">LISTE DES COPROPRIÉTÉS ET ATTRIBUTION DES RÔLES</h2>
            
            <div className="rounded-lg border border-rgpd-blue-light/40 bg-rgpd-blue-light/5 p-4">
              {/* ... existing filters code ... */}
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-sm font-semibold text-rgpd-blue-light">Filtrer vos copropriétés selon leurs performances de vente :</p>
                  <p className="text-xs text-slate-500">Période disponible jusqu'à {lastYear ?? '…'}. Données issues de la DVF.</p>
                </div>
                <div className="min-w-[220px]">
                  <SelectMe
                    multiple
                    closeOnSelect={false}
                    options={yearsOptions.map((option) => ({ value: Number(option.value), label: option.label }))}
                    value={selectedYears.map((year) => ({ value: year, label: String(year) }))}
                    onChange={(optionsSelected) => {
                      const arr = Array.isArray(optionsSelected) ? optionsSelected : [];
                      setSelectedYears(arr.map((item) => Number(item.value)).filter((val) => !Number.isNaN(val)));
                    }}
                    placeholder="Sélectionner une ou plusieurs années"
                    listPosition="bottom"
                    listMaxHeight={260}
                  />
                </div>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="flex items-center gap-2 text-sm">
                  <label className="font-medium text-slate-700">Nb ventes min</label>
                  <input
                    type="number"
                    className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-rgpd-blue-light focus:ring-1 focus:ring-rgpd-blue-light focus:outline-none"
                    value={filters.ventesMin ?? ''}
                    onChange={(event) => setFilters((previous) => ({ ...previous, ventesMin: event.target.value !== '' ? Number(event.target.value) : null }))}
                    placeholder="Min"
                    min={0}
                  />
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <label className="font-medium text-slate-700">Nb ventes max</label>
                  <input
                    type="number"
                    className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-rgpd-blue-light focus:ring-1 focus:ring-rgpd-blue-light focus:outline-none"
                    value={filters.ventesMax ?? ''}
                    onChange={(event) => setFilters((previous) => ({ ...previous, ventesMax: event.target.value !== '' ? Number(event.target.value) : null }))}
                    placeholder="Max"
                    min={0}
                  />
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <label className="font-medium text-slate-700">CA min</label>
                  <input
                    type="number"
                    className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-rgpd-blue-light focus:ring-1 focus:ring-rgpd-blue-light focus:outline-none"
                    value={filters.caMin ?? ''}
                    onChange={(event) => setFilters((previous) => ({ ...previous, caMin: event.target.value !== '' ? Number(event.target.value) : null }))}
                    placeholder="Min €"
                    min={0}
                  />
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <label className="font-medium text-slate-700">CA max</label>
                  <input
                    type="number"
                    className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-rgpd-blue-light focus:ring-1 focus:ring-rgpd-blue-light focus:outline-none"
                    value={filters.caMax ?? ''}
                    onChange={(event) => setFilters((previous) => ({ ...previous, caMax: event.target.value !== '' ? Number(event.target.value) : null }))}
                    placeholder="Max €"
                    min={0}
                  />
                </div>
              </div>
            <div className="mt-4 border-t border-slate-200 pt-3">
              <p className="mb-2 text-sm font-semibold text-rgpd-blue-light">Afficher uniquement les copropriétés sans rôle attribué :</p>
              <div className="flex flex-wrap gap-2">
                {roles.map((role) => {
                  const isActive = missingRoleFilters.has(role.value);
                  return (
                    <button
                      key={role.value}
                      type="button"
                      onClick={() => toggleMissingRoleFilter(role.value)}
                      className={classNames(
                        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                        isActive
                          ? 'border-rgpd-blue-light bg-rgpd-blue-light text-white'
                          : 'border-slate-300 bg-white text-slate-600 hover:border-rgpd-blue-light hover:text-rgpd-blue-light',
                      )}
                    >
                      Sans {role.label.toLowerCase()}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              {yearsStatus === 'PENDING' && <span>Chargement des années disponibles…</span>}
              {yearsStatus === 'ERROR' && <span className="text-red-500">Impossible de récupérer l'année DVF la plus récente.</span>}
              {isStatsLoading && (
                <span className="inline-flex items-center gap-2">
                  <Spinner size="sm" />
                  {coproStats.size > 0
                    ? 'Actualisation des statistiques…'
                    : 'Chargement des statistiques…'}
                </span>
              )}
              {!isStatsLoading && statsError && <span className="text-red-500">{statsError}</span>}
              {!isStatsLoading && !statsError && coproStats.size === 0 && (
                <span className="text-amber-600">Aucune donnée de ventes disponible pour vos copropriétés.</span>
              )}
                <button
                  type="button"
                  className="ml-auto rounded border border-slate-400 px-3 py-1 text-xs text-slate-600 hover:bg-slate-100"
                  onClick={() => {
                    setFilters({ ventesMin: null, ventesMax: null, caMin: null, caMax: null });
                    setMissingRoleFilters(new Set());
                  }}
                >
                  Réinitialiser les filtres
                </button>
              </div>
            <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-600">
              <span className="font-medium">Total copropriétés : {totalCopros}</span>
              <span className="font-medium">Affichées : {totalVisible}</span>
              <span className="font-medium">Sélectionnées : {selectedInFilterCount}</span>
            </div>
            </div>
          
            <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
              <p className="mb-2 font-semibold text-rgpd-blue-dark">6 rôles peuvent être attribués :</p>
              <ul className="list-inside list-disc space-y-1 text-sm text-gray-700">
                <li><span className="font-medium">Référent publique :</span> Personne utilisée dans les signatures de mail et dans l'image de marque reliée à cette copropriété.</li>
                <li><span className="font-medium">Chargé qualif DPE :</span> Personne en charge de qualifier les DPEs de cette copropriété.</li>
                <li><span className="font-medium">Gestionnaire de l'immeuble :</span> Responsable principal de la copropriété.</li>
                <li><span className="font-medium">Transactionnaire :</span> Responsable des ventes et achats.</li>
                <li><span className="font-medium">Gestionnaire locatif :</span> Responsable des locations et des locataires.</li>
                <li><span className="font-medium">Intervenant extérieur :</span> Représente généralement un responsable de la supervision et de l'inspection des travaux, ou tout autre personne pertinente pour la copropriété intervenant depuis une entreprise tierce.</li>
              </ul>
            </div>
          </div>
          
          {(status === 'PENDING' || status === 'IDLE') && (
            <div className="mx-10 my-32 text-center">
              <p className="mx-10 my-32 text-center">
                <Spinner aria-label="Center-aligned spinner" size="xl" />
              </p>
            </div>
          )}
          {status === 'SUCCESS' && copros.length > 0 && companyUsers.length > 0 && (
            <>
              <div className="mb-4 flex-wrap justify-start gap-2 md:flex">

                <button
                  onClick={handleSelectAll}
                  disabled={selectAllDisabled}
                  className={classNames(
                    'self-end h-fit outline-1 outline-rgpd-blue-light text-rgpd-blue-light px-4 py-2 mt-4 rounded w-full md:w-auto',
                    selectAllDisabled && 'cursor-not-allowed opacity-60',
                  )}
                >
                  {selectAllLabel}
                </button>
                <button onClick={() => setIsPopupOpen(true)} className="mt-4 h-fit w-full self-end rounded bg-rgpd-blue-light px-4 py-2 text-white md:w-auto">
                  Attribuer un rôle en lot
                </button>
                <button
                  onClick={() => setIsChooserOpen(true)}
                  className="mt-4 h-fit w-full self-end rounded bg-rgpd-blue-dark px-4 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50 md:w-auto"
                  disabled={isSmartAttributionButtonDisabled}
                  title={smartAttributionButtonTitle}
                >
                  {isSmartAttributionPending ? 'Attribution intelligente…' : 'Attribution intelligente'}
                </button>
                <div className="grow" />
                <a href="https://app.declarations-juridiques.fr/compliance/collaborator/management" target="_blank" rel="noreferrer">
                  <button className="mt-4 w-full rounded bg-rgpd-blue-dark px-4 py-2 text-sm text-white md:w-auto">
                    Editer les collaborateurs depuis
                    <br />
                    <span className="italic">Déclarations Juridiques</span>
                  </button>
                </a>
              </div>
              {filteredEntries.length === 0 && (
                <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                  Aucune copropriété ne correspond aux filtres saisis pour la période sélectionnée.
                </div>
              )}
              <div className="overflow-x-visible" ref={tableRef}>
                <Table
                  sortableColumns={sortableColumns}
                  isSearchable
                  data={tableRows.map((row) => {
                    const {
                      [salesCountHeader]: rawCount,
                      [salesAmountHeader]: rawAmount,
                      [`${salesCountHeader}__display`]: countDisplay,
                      [`${salesAmountHeader}__display`]: amountDisplay,
                      ...rest
                    } = row;
                    return {
                      ...rest,
                      [salesCountHeader]: countDisplay,
                      [`${salesCountHeader}__raw`]: rawCount,
                      [salesAmountHeader]: amountDisplay,
                      [`${salesAmountHeader}__raw`]: rawAmount,
                    };
                  })}
                  headers={headers}
                  tableClass="min-w-full bg-white border text-sm"
                  columnChecking
                  idKey="id"
                  dataChangeCallback={handleTableDataChange}
                  setParentFilteredData={handleTableFilteredData}
                  validColumnLabel="#"
                  mergedCellsConfig={mergedCellsConfig}
                  stickyHeader
                />
                <button
                  onClick={handleSelectAll}
                  disabled={selectAllDisabled}
                  className={classNames(
                    'outline-1 outline-rgpd-blue-light text-rgpd-blue-light mr-4 px-4 py-2 rounded mt-4 mx-auto w-full md:w-auto',
                    selectAllDisabled && 'cursor-not-allowed opacity-60',
                  )}
                >
                  {selectAllLabel}
                </button>
                <button onClick={() => setIsPopupOpen(true)} className="mx-auto mt-4 w-full rounded bg-rgpd-blue-light px-4 py-2 text-white md:w-auto">
                  Attribuer un rôle en lot
                </button>
              </div>
            </>
          )}
          {status === 'SUCCESS' && copros.length === 0 && (
            <div className="mt-8 w-full bg-white pb-2">
              <div className="flex w-full place-content-center">
                <Badge
                  mode="warning"
                  text="Aucune copropriété trouvée"
                  className="my-3"
                />
              </div>
            </div>
          )}
          {status === 'SUCCESS' && companyUsers.length === 0 && (
            <div className="mt-8 w-full bg-white pb-2">
              <div className="flex w-full place-content-center">
                <Badge
                  mode="warning"
                  text="Aucun utilisateur trouvé"
                  className="my-3"
                />
              </div>
            </div>
          )}
          {status === 'ERROR' && (
            <Popup
              isOpen={!!errorMessage}
              onClose={() => setErrorMessage(null)}
              title="Erreur"
              children={(
                <Badge
                  mode="error"
                  text={errorMessage}
                />
              )}
            />
          )}
          {batchErrors.length > 0 && (
            <Popup
              isOpen={batchErrors.length > 0}
              onClose={() => setBatchErrors([])}
              title="Erreur lors de la mise à jour de(s) copro(s)"
              children={(
                <div className="text-red-500">
                  <Badge
                    mode="error"
                    text="Une erreur est survenue lors de la mise à jour de(s) copro(s) :"
                  />
                  <ul>
                    {batchErrors.map((copro, index) => (
                      <li key={index}>{copro}</li>
                    ))}
                  </ul>
                </div>
              )}
            />
          )}
          <div ref={bottomRef} style={{ height: '1px' }} />
          {isCoproDataLoaded && renderSaveButton()}
      <AttributionChooserDialog
        isOpen={isChooserOpen}
        onClose={() => setIsChooserOpen(false)}
        onSelect={(mode) => {
          setIsChooserOpen(false);
          if (mode === 'geo') {
            setIsGeoDialogOpen(true);
          } else {
            setIsPerformanceDialogOpen(true);
          }
        }}
        disabled={isSmartAttributionPending}
      />
      <SmartAttributionDialog
        isOpen={isGeoDialogOpen}
        onClose={() => setIsGeoDialogOpen(false)}
        companyUsers={companyUsers}
        companyId={company?.id ?? null}
        jwt={jwt}
        salesEntries={smartAttributionSalesEntries}
        totalCoprosCount={totalCopros}
        onApply={async(assignments) => {
          await applySmartAttribution(assignments);
        }}
      />
      <PerformanceAttributionDialog
        isOpen={isPerformanceDialogOpen}
        onClose={() => setIsPerformanceDialogOpen(false)}
        companyUsers={companyUsers}
        companyId={company?.id ?? null}
        jwt={jwt}
        salesEntries={smartAttributionSalesEntries}
        totalCoprosCount={totalCopros}
        onApply={async(assignments) => {
          await applySmartAttribution(assignments);
        }}
      />
          <Popup
            isOpen={isPopupOpen}
            onClose={() => setIsPopupOpen(false)}
            title="Attribuer un rôle en lot"
            disableCloseButton={isBatchLoading}
            children={(
              <div>
                {isBatchLoading ? (
                  <p className="mx-10 my-32 text-center">
                    <Spinner aria-label="Center-aligned spinner" size="xl" />
                  </p>
                ) : (
                  <>
                    <div className="my-3 mb-12">
                      <label className="mb-2 block font-semibold" htmlFor="batch-role">
                        Rôle :
                      </label>
                      <select
                        id="batch-role"
                        className="w-full min-w-72 rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none sm:min-w-96"
                        value={selectedRole ?? ''}
                        onChange={(event) => setSelectedRole(event.target.value || null)}
                      >
                        {roles.map((role) => (
                          <option key={role.value} value={role.value}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="my-3 mb-24">
                      <label className="mb-2 block font-semibold" htmlFor="batch-user">
                        {isReferentPubliqueRoleSelected ? 'Référent publique :' : 'Utilisateur :'}
                      </label>
                      {isReferentPubliqueRoleSelected ? (
                        <select
                          id="batch-user"
                          className="w-full min-w-72 rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none sm:min-w-96"
                          value={selectedBatchBrandingReferent}
                          onChange={(event) => setSelectedBatchBrandingReferent(event.target.value)}
                        >
                          {batchUserOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <select
                          id="batch-user"
                          className="w-full min-w-72 rounded border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none sm:min-w-96"
                          value={selectedUser != null ? String(selectedUser) : ''}
                          onChange={(event) => {
                            const value = event.target.value;
                            setSelectedUser(value === '' ? null : Number(value));
                          }}
                        >
                          {batchUserOptions.map((userOption) => (
                            <option key={userOption.value} value={userOption.value}>
                              {userOption.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    {!isReferentPubliqueRoleSelected && selectedRole != null && (
                      <div className="mb-6 rounded border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-600">
                        {isMultiRole(selectedRole) ? (
                          selectedUser == null ? (
                            <span className="text-amber-700">
                              Tous les collaborateurs attribués à ce rôle seront retirés des copropriétés sélectionnées.
                            </span>
                          ) : (
                            <span>
                              Ce rôle accepte plusieurs personnes. Le collaborateur sélectionné sera <strong>ajouté</strong> aux attributions existantes.
                              Pour retirer tous les collaborateurs de ce rôle, sélectionnez <strong>« Désattribuer »</strong>.
                            </span>
                          )
                        ) : (
                          selectedUser == null ? (
                            <span className="text-amber-700">
                              Le collaborateur actuellement attribué à ce rôle sera retiré des copropriétés sélectionnées.
                            </span>
                          ) : (
                            <span>
                              Le collaborateur actuellement attribué à ce rôle sera <strong>remplacé</strong> par le nouveau sur les copropriétés sélectionnées.
                            </span>
                          )
                        )}
                      </div>
                    )}
                    <center>
                      <button onClick={handleBatchAssign} className="w-full rounded bg-rgpd-blue-light px-4 py-2 text-white md:w-auto">
                        Attribuer
                      </button>
                    </center>
                  </>
                )}
              </div>
            )}
          />
        </>
      )}
    />
  );
};

export default ActivitySetupStepCollaborator;